import { useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  BarChart2,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Layers,
  Kanban,
  ArrowRight,
  Bug,
} from 'lucide-react'
import { useSpace, useIssues, useStatuses, useIssueMutations } from '../hooks'
import { cn } from '../utils'
import { IssueModal } from '../components/IssueModal'
import type { Issue } from '../api'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { cardClassName } from '@jira-clone/ui'

export function ReportsPage() {
  const navigate = useNavigate()
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedIssueId = searchParams.get('selectedIssue')

  const { data: space } = useSpace(spaceId ?? null)
  const { data: issues = [], isLoading } = useIssues(spaceId ?? null)
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: userOptions = [] } = useUserOptions(spaceId ?? null)
  const { update, remove } = useIssueMutations(spaceId ?? null)

  const selectedIssue = useMemo(() => {
    if (!selectedIssueId) return null
    return (issues as Issue[]).find((i) => i.id === selectedIssueId || i.key === selectedIssueId) ?? null
  }, [issues, selectedIssueId])

  const handleOpenIssue = (issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }

  const handleCloseIssueModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  const stats = useMemo(() => {
    const total = issues.length
    const done = issues.filter((i: any) => i.status_category === 'done').length
    const inProgress = issues.filter((i: any) => i.status_category === 'in_progress').length
    const todo = issues.filter((i: any) => i.status_category === 'todo').length
    const completionRate = total > 0 ? Math.round((done / total) * 100) : 0

    const highPrioIssues = issues.filter((i: any) => i.priority === 'high' || i.priority === 'critical')
    const bugIssues = issues.filter((i: any) => i.type === 'bug')

    return {
      total,
      done,
      inProgress,
      todo,
      completionRate,
      highPrioCount: highPrioIssues.length,
      bugsCount: bugIssues.length,
      highPrioIssues: highPrioIssues.slice(0, 5),
      bugIssues: bugIssues.slice(0, 5),
    }
  }, [issues])

  const priorityCounts = useMemo(() => {
    const map: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const i of issues as any[]) {
      if (map[i.priority] !== undefined) map[i.priority]++
    }
    return map
  }, [issues])

  const typeCounts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const i of issues as any[]) {
      map[i.type] = (map[i.type] || 0) + 1
    }
    return map
  }, [issues])

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6 bg-white dark:bg-slate-950 space-y-6 font-sans">
      {/* Title & Quick Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-blue-600" />
            Reports & Analytics — {space?.name || 'Space'}
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Real-time project health, issue distributions, and velocity
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/spaces/${spaceId}/board`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-800 text-xs font-semibold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-900 shadow-2xs"
          >
            <Kanban className="h-3.5 w-3.5 text-blue-600" /> View Board
          </button>
          <button
            onClick={() => navigate(`/spaces/${spaceId}/list`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-xs font-semibold text-white hover:bg-blue-700 shadow-xs"
          >
            View All Issues <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Top 4 Stat Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div
          onClick={() => navigate(`/spaces/${spaceId}/list`)}
          className={cn(cardClassName({ interactive: true }), 'p-4 hover:border-blue-300 transition-all')}
        >
          <div className="flex items-center justify-between text-gray-500 dark:text-slate-400 mb-1">
            <span className="text-xs font-medium">Total Work Items</span>
            <Layers className="h-4 w-4 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.total}</p>
          <p className="text-[11px] text-gray-400 mt-1">{space?.key} project total</p>
        </div>

        <div
          onClick={() => navigate(`/spaces/${spaceId}/board`)}
          className={cn(cardClassName({ interactive: true }), 'p-4 hover:border-blue-300 transition-all')}
        >
          <div className="flex items-center justify-between text-gray-500 dark:text-slate-400 mb-1">
            <span className="text-xs font-medium">In Progress</span>
            <Clock className="h-4 w-4 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.inProgress}</p>
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">Active tasks in flight</p>
        </div>

        <div
          onClick={() => navigate(`/spaces/${spaceId}/list`)}
          className={cn(cardClassName({ interactive: true }), 'p-4 hover:border-blue-300 transition-all')}
        >
          <div className="flex items-center justify-between text-gray-500 dark:text-slate-400 mb-1">
            <span className="text-xs font-medium">Completed</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.done}</p>
          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">Resolved & closed</p>
        </div>

        <div className={cn(cardClassName(), 'p-4')}>
          <div className="flex items-center justify-between text-gray-500 dark:text-slate-400 mb-1">
            <span className="text-xs font-medium">Completion Rate</span>
            <TrendingUp className="h-4 w-4 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.completionRate}%</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
              style={{ width: `${stats.completionRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Progress & Breakdown Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Status Breakdown */}
        <div className={cn(cardClassName(), 'p-5')}>
          <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-3">Status Distribution</h3>
          {stats.total === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center">No issues in space yet</p>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                  <span>To Do ({stats.todo})</span>
                  <span>{Math.round((stats.todo / stats.total) * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-gray-400 rounded-full"
                    style={{ width: `${(stats.todo / stats.total) * 100}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                  <span>In Progress ({stats.inProgress})</span>
                  <span>{Math.round((stats.inProgress / stats.total) * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                  <span>Done ({stats.done})</span>
                  <span>{Math.round((stats.done / stats.total) * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${(stats.done / stats.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Priority Breakdown */}
        <div className={cn(cardClassName(), 'p-5')}>
          <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-3">Priority Breakdown</h3>
          <div className="space-y-2 text-xs">
            {Object.entries(priorityCounts).map(([prio, count]) => (
              <div
                key={prio}
                className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-slate-800/60 last:border-0"
              >
                <span className="capitalize font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      prio === 'critical'
                        ? 'bg-red-600'
                        : prio === 'high'
                        ? 'bg-orange-500'
                        : prio === 'medium'
                        ? 'bg-amber-400'
                        : 'bg-blue-400',
                    )}
                  />
                  {prio}
                </span>
                <span className="font-semibold text-gray-900 dark:text-slate-100">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Critical Items & Recent Bugs */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Urgent / High Priority */}
        <div className={cn(cardClassName(), 'p-5')}>
          <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-red-500" />
            High & Critical Priority Items ({stats.highPrioCount})
          </h3>
          {stats.highPrioIssues.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No high priority blockers! 🎉</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800 text-xs">
              {stats.highPrioIssues.map((issue: any) => (
                <div
                  key={issue.id}
                  onClick={() => handleOpenIssue(issue.id)}
                  className="flex items-center justify-between py-2 cursor-pointer hover:bg-red-50/40 dark:hover:bg-red-950/20 rounded px-2 -mx-2 transition-colors group"
                >
                  <div className="flex items-center gap-2 truncate pr-2">
                    <span className="font-mono text-[10px] text-red-600 dark:text-red-400 font-bold shrink-0">
                      {issue.key}
                    </span>
                    <span className="truncate font-medium text-gray-800 dark:text-slate-200 group-hover:text-red-600">
                      {issue.title}
                    </span>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 px-1.5 py-0.5">
                    {issue.priority}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bug Tracker */}
        <div className={cn(cardClassName(), 'p-5')}>
          <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2 mb-3">
            <Bug className="h-4 w-4 text-orange-500" />
            Reported Bugs ({stats.bugsCount})
          </h3>
          {stats.bugIssues.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">Zero bugs reported! 🚀</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800 text-xs">
              {stats.bugIssues.map((issue: any) => (
                <div
                  key={issue.id}
                  onClick={() => handleOpenIssue(issue.id)}
                  className="flex items-center justify-between py-2 cursor-pointer hover:bg-orange-50/40 dark:hover:bg-orange-950/20 rounded px-2 -mx-2 transition-colors group"
                >
                  <div className="flex items-center gap-2 truncate pr-2">
                    <span className="font-mono text-[10px] text-orange-600 dark:text-orange-400 font-bold shrink-0">
                      {issue.key}
                    </span>
                    <span className="truncate font-medium text-gray-800 dark:text-slate-200 group-hover:text-orange-600">
                      {issue.title}
                    </span>
                  </div>
                  <span className="shrink-0 text-[10px] font-semibold rounded bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 text-gray-600 dark:text-slate-400">
                    {issue.status_category}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deep-linked Issue Modal */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          open={!!selectedIssue}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={userOptions}
          epics={(issues as Issue[]).filter((i) => i.type === 'epic')}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}
    </div>
  )
}
